import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const ImportPlayersModal = ({ isOpen, onClose, onImport, teamId, teamName }) => {
  const [file, setFile] = useState(null);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);

  const handleFileChange = (e) => {
    const selectedFile = e?.target?.files?.[0];
    if (!selectedFile) return;

    if (!selectedFile?.name?.endsWith('.csv')) {
      setError('Please select a valid CSV file');
      return;
    }

    setFile(selectedFile);
    setError('');
    parseCSV(selectedFile);
  };

  const parseCSV = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e?.target?.result;
        const lines = text?.split('\n')?.filter(line => line?.trim());
        
        if (lines?.length < 2) {
          setError('CSV file is empty or invalid');
          return;
        }

        const headers = lines?.[0]?.split(',')?.map(h => h?.trim()?.toLowerCase());
        const requiredHeaders = ['name', 'role', 'batting_style', 'bowling_style', 'jersey_number'];
        
        const missingHeaders = requiredHeaders?.filter(h => !headers?.includes(h));
        if (missingHeaders?.length > 0) {
          setError(`Missing required columns: ${missingHeaders?.join(', ')}`);
          return;
        }

        const data = [];
        for (let i = 1; i < lines?.length; i++) {
          const values = lines?.[i]?.split(',')?.map(v => v?.trim());
          const row = {};
          headers?.forEach((header, index) => {
            row[header] = values?.[index] || '';
          });
          
          if (row?.name && row?.role) {
            data?.push(row);
          }
        }

        if (data?.length === 0) {
          setError('No valid player data found in CSV');
          return;
        }

        setPreview(data?.slice(0, 5));
        setError('');
      } catch (err) {
        setError('Failed to parse CSV file. Please check the format.');
        console.error('CSV parsing error:', err);
      }
    };
    reader?.readAsText(file);
  };

  const handleImport = async () => {
    if (!file || preview?.length === 0) return;

    setIsProcessing(true);
    const reader = new FileReader();
    
    reader.onload = async (e) => {
      try {
        const text = e?.target?.result;
        const lines = text?.split('\n')?.filter(line => line?.trim());
        const headers = lines?.[0]?.split(',')?.map(h => h?.trim()?.toLowerCase());
        
        const players = [];
        for (let i = 1; i < lines?.length; i++) {
          const values = lines?.[i]?.split(',')?.map(v => v?.trim());
          const row = {};
          headers?.forEach((header, index) => {
            row[header] = values?.[index] || '';
          });

          if (!row?.name || !row?.role) continue;

          const validRoles = ['batter', 'bowler', 'all-rounder', 'wicket-keeper'];
          const role = row?.role?.toLowerCase()?.replace(/\s+/g, '-');
          
          if (!validRoles?.includes(role)) {
            console.warn(`Skipping player ${row?.name}: Invalid role ${row?.role}`);
            continue;
          }

          // Map lowercase role to database enum format
          const roleMap = {
            'batter': 'Batter',
            'bowler': 'Bowler',
            'all-rounder': 'All-rounder',
            'wicket-keeper': 'Wicket Keeper'
          };

          players?.push({
            name: row?.name,
            role: roleMap?.[role],
            battingStyle: row?.batting_style || 'right-hand',
            bowlingStyle: row?.bowling_style || 'none',
            jerseyNumber: row?.jersey_number ? parseInt(row?.jersey_number) : null,
            teamId: teamId
          });
        }

        if (players?.length === 0) {
          setError('No valid players found. Please check role, batting style, and bowling style values.');
          setIsProcessing(false);
          return;
        }

        await onImport(players);
        handleClose();
      } catch (err) {
        setError('Failed to import players. Please try again.');
        console.error('Import error:', err);
      } finally {
        setIsProcessing(false);
      }
    };

    reader?.readAsText(file);
  };

  const handleClose = () => {
    setFile(null);
    setError('');
    setPreview([]);
    setIsProcessing(false);
    onClose();
  };

  const downloadTemplate = () => {
    const headers = 'name,role,batting_style,bowling_style,jersey_number';
    const example1 = 'Virat Kohli,batsman,right-hand,right-arm-medium,18';
    const example2 = 'Jasprit Bumrah,bowler,right-hand,right-arm-fast,93';
    const example3 = 'Ravindra Jadeja,all-rounder,left-hand,left-arm-spin,8';
    const csv = `${headers}\n${example1}\n${example2}\n${example3}`;
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL?.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'players_template.csv';
    a?.click();
    window.URL?.revokeObjectURL(url);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1200] p-4">
      <div className="bg-card border border-border rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border p-4 md:p-6 flex items-center justify-between">
          <div>
            <h3 className="text-lg md:text-xl font-semibold text-foreground">Import Players from CSV</h3>
            <p className="text-sm text-muted-foreground mt-1">{teamName}</p>
          </div>
          <button
            onClick={handleClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        <div className="p-4 md:p-6 space-y-6">
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Icon name="Info" size={20} className="text-blue-600 dark:text-blue-400 mt-0.5" />
              <div className="flex-1">
                <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-2">CSV Format Requirements</h4>
                <ul className="text-sm text-blue-800 dark:text-blue-200 space-y-1">
                  <li>• Required columns: name, role, batting_style, bowling_style, jersey_number</li>
                  <li>• Role values: batsman, bowler, all-rounder, wicket-keeper</li>
                  <li>• Batting style: right-hand, left-hand</li>
                  <li>• Bowling style: none, right-arm-fast, left-arm-fast, right-arm-medium, left-arm-medium, right-arm-spin, left-arm-spin</li>
                  <li>• Jersey number: numeric value (optional)</li>
                </ul>
                <Button
                  variant="link"
                  onClick={downloadTemplate}
                  iconName="Download"
                  iconPosition="left"
                  className="mt-2 text-blue-600 dark:text-blue-400 p-0 h-auto"
                >
                  Download Template CSV
                </Button>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Select CSV File
            </label>
            <div className="border-2 border-dashed border-border rounded-lg p-8 text-center hover:border-primary transition-colors">
              <input
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                className="hidden"
                id="csv-upload"
              />
              <label
                htmlFor="csv-upload"
                className="cursor-pointer flex flex-col items-center gap-3"
              >
                <Icon name="Upload" size={48} className="text-muted-foreground" />
                <div>
                  <p className="text-foreground font-medium">Click to upload CSV file</p>
                  <p className="text-sm text-muted-foreground mt-1">or drag and drop</p>
                </div>
              </label>
            </div>
            {file && (
              <div className="mt-3 flex items-center gap-2 text-sm text-foreground">
                <Icon name="FileText" size={16} />
                <span>{file?.name}</span>
              </div>
            )}
          </div>

          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Icon name="AlertCircle" size={20} className="text-red-600 dark:text-red-400 mt-0.5" />
                <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
              </div>
            </div>
          )}

          {preview?.length > 0 && (
            <div>
              <h4 className="font-medium text-foreground mb-3">Preview (First 5 players)</h4>
              <div className="border border-border rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted">
                      <tr>
                        <th className="px-4 py-2 text-left font-medium text-foreground">Name</th>
                        <th className="px-4 py-2 text-left font-medium text-foreground">Role</th>
                        <th className="px-4 py-2 text-left font-medium text-foreground">Batting</th>
                        <th className="px-4 py-2 text-left font-medium text-foreground">Bowling</th>
                        <th className="px-4 py-2 text-left font-medium text-foreground">Jersey #</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview?.map((row, index) => (
                        <tr key={index} className="border-t border-border">
                          <td className="px-4 py-2 text-foreground">{row?.name}</td>
                          <td className="px-4 py-2 text-muted-foreground">{row?.role}</td>
                          <td className="px-4 py-2 text-muted-foreground">{row?.batting_style}</td>
                          <td className="px-4 py-2 text-muted-foreground">{row?.bowling_style}</td>
                          <td className="px-4 py-2 text-muted-foreground">{row?.jersey_number || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="sticky bottom-0 bg-card border-t border-border p-4 md:p-6 flex flex-col sm:flex-row gap-3 justify-end">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isProcessing}
            className="w-full sm:w-auto"
          >
            Cancel
          </Button>
          <Button
            variant="default"
            onClick={handleImport}
            disabled={!file || preview?.length === 0 || isProcessing}
            iconName={isProcessing ? 'Loader' : 'Upload'}
            iconPosition="left"
            className="w-full sm:w-auto"
          >
            {isProcessing ? 'Importing...' : 'Import Players'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ImportPlayersModal;